import { FC, useCallback } from 'react';
import { PreloadedQuery, useMutation, usePreloadedQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Box from '@mui/material/Box';
import Breadcrumbs from 'components/Breadcrumbs';
import Form, { FormFields } from 'components/GeneralSettingsEditForm';

import { stringToNumber, useErrorHandler, withData } from 'lib';
import { SettingsGeneralPageQuery } from '__generated__/SettingsGeneralPageQuery.graphql';
import { SettingsGeneralPageMutation } from '__generated__/SettingsGeneralPageMutation.graphql';

const query = graphql`
  query SettingsGeneralPageQuery {
    settings {
      ...GeneralSettingsEditForm_settings
    }
  }
`;

type Props = {
  preloadedQuery: PreloadedQuery<SettingsGeneralPageQuery>;
};

const SettingsGeneralPage: FC<Props> = ({ preloadedQuery }) => {
  const errorHandler = useErrorHandler();

  const data = usePreloadedQuery<SettingsGeneralPageQuery>(
    query,
    preloadedQuery
  );

  const [update, loading] = useMutation<SettingsGeneralPageMutation>(
    graphql`
      mutation SettingsGeneralPageMutation($input: UpdateSettingsInput!) {
        updateSettings(input: $input) {
          ...GeneralSettingsEditForm_settings
        }
      }
    `
  );

  const handleSubmit = useCallback(
    (values: FormFields) => {
      update({
        variables: {
          input: {
            ...values,
            abandonedCheckoutTimeout: stringToNumber(
              values.abandonedCheckoutTimeout
            ),
            advancePayment: stringToNumber(values.advancePayment),
            defaultTransferPrice: stringToNumber(values.defaultTransferPrice),
            autosaveDelay: stringToNumber(values.autosaveDelay),
            cancelOrdersAfterMinutes: stringToNumber(
              values.cancelOrdersAfterMinutes
            ),
            flightsApiLimit: stringToNumber(values.flightsApiLimit),
            infantTripPrice: stringToNumber(values.infantTripPrice),
            orderCleanupAge: stringToNumber(values.orderCleanupAge),
            priceChangeTreshold: stringToNumber(values.priceChangeTreshold),
            priceCheckerLimit: stringToNumber(values.priceCheckerLimit),
            revalidateStaticContent: stringToNumber(
              values.revalidateStaticContent
            ),
            smtpPort: stringToNumber(values.smtpPort),
            standardMarkupAdult: stringToNumber(values.standardMarkupAdult),
            standardMarkupChild: stringToNumber(values.standardMarkupChild),
            updateFlightsPerMinute: stringToNumber(
              values.updateFlightsPerMinute
            ),
            keepFlightPriceHistoryDays: stringToNumber(
              values.keepFlightPriceHistoryDays
            ),
            inactiveUpdaterWarningThreshold: stringToNumber(
              values.inactiveUpdaterWarningThreshold
            ),
          },
        },
        ...errorHandler({
          successMessage: 'Nustatymai išsaugoti',
        }),

        updater: store => {
          store.get('client:root:settings')?.invalidateRecord();
        },
      });
    },
    [errorHandler, update]
  );

  return (
    <Box>
      <Breadcrumbs
        breadcrumbs={[
          { name: 'Nustatymai', link: '/settings' },
          { name: 'Bendri nustatymai' },
        ]}
      />

      <Form
        settings={data.settings}
        loading={loading}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default withData<SettingsGeneralPageQuery>({
  query,
})(SettingsGeneralPage);
